/**
 * Server-sent events over `fetch` + a byte stream, shared by the console and the E2E suites. EventSource cannot set
 * headers and the token must never travel in the URL, so the stream is read by hand: `id:`, `event:`, `data:`
 * (multi-line), `retry:` and comment heartbeats, as the WHATWG HTML spec ("Server-sent events") describes.
 *
 * Two layers: `createSseParser` is a pure incremental parser (text in, messages out); `readSseStream` feeds it from a
 * response body and stops on an AbortSignal.
 */
export interface SseMessage {
  /** Last `id:` seen on the stream (the spec's "last event ID buffer"), null until one arrives. */
  id: string | null;
  /** The `event:` name; `message` when the block has none. */
  event: string;
  /** The `data:` lines joined with `\n`. */
  data: string;
}

export interface SseParserHandlers {
  onMessage(message: SseMessage): void;
  /** A valid `retry:` field (reconnection delay in ms). */
  onRetry?(ms: number): void;
}

export interface SseHandlers extends SseParserHandlers {
  /** Any bytes received, including heartbeats: used for the idle watchdog. */
  onChunk?(): void;
}

export interface SseParser {
  /** Feeds decoded text; any chunking of the stream gives the same messages. */
  push(text: string): void;
  /** The stream ended: dispatches a pending block that was not closed by a blank line. */
  end(): void;
}

/** The part of a ReadableStream the reader needs (so no DOM lib is required). */
export interface SseByteStream {
  getReader(): { read(): Promise<{ done: boolean; value?: Uint8Array }>; cancel(): Promise<unknown> };
}

interface Block {
  lastId: string | null;
  event: string;
  data: string[];
}

/** Splits `field: value` (one optional space after the colon is dropped). A line without a colon is a field with no value. */
export function splitSseLine(line: string): { field: string; value: string } {
  const colon = line.indexOf(':');
  if (colon === -1) return { field: line, value: '' };
  const value = line.slice(colon + 1);
  return { field: line.slice(0, colon), value: value.startsWith(' ') ? value.slice(1) : value };
}

function applyField(block: Block, field: string, value: string, onRetry: SseParserHandlers['onRetry']): void {
  if (field === 'event') block.event = value;
  else if (field === 'data') block.data.push(value);
  else if (field === 'id') {
    if (!value.includes('\0')) block.lastId = value;
  } else if (field === 'retry') {
    if (/^\d+$/.test(value)) onRetry?.(Number(value));
  } // unknown fields are ignored per spec
}

function dispatch(block: Block, handlers: SseParserHandlers): void {
  if (block.data.length > 0) handlers.onMessage({ id: block.lastId, event: block.event || 'message', data: block.data.join('\n') });
  block.event = '';
  block.data = [];
}

function processLine(block: Block, rawLine: string, handlers: SseParserHandlers): void {
  const line = rawLine.endsWith('\r') ? rawLine.slice(0, -1) : rawLine;
  if (line === '') return dispatch(block, handlers);
  if (line.startsWith(':')) return; // comment / heartbeat
  const { field, value } = splitSseLine(line);
  applyField(block, field, value, handlers.onRetry);
}

export function createSseParser(handlers: SseParserHandlers): SseParser {
  const block: Block = { lastId: null, event: '', data: [] };
  let buffer = '';
  return {
    push(text) {
      buffer += text;
      let nl = buffer.indexOf('\n');
      while (nl >= 0) {
        const line = buffer.slice(0, nl);
        buffer = buffer.slice(nl + 1);
        processLine(block, line, handlers);
        nl = buffer.indexOf('\n');
      }
    },
    end() {
      dispatch(block, handlers);
    },
  };
}

/** Reads `body` until it ends or `signal` aborts (the reader is cancelled, which ends the loop without an error). */
export async function readSseStream(body: SseByteStream, handlers: SseHandlers, signal: AbortSignal): Promise<void> {
  const reader = body.getReader();
  const decoder = new TextDecoder();
  const parser = createSseParser(handlers);
  const onAbort = () => void reader.cancel().catch(() => undefined);
  signal.addEventListener('abort', onAbort, { once: true });
  try {
    for (;;) {
      const { value, done } = await reader.read();
      if (done) break;
      handlers.onChunk?.();
      parser.push(decoder.decode(value, { stream: true }));
    }
    parser.end();
  } finally {
    signal.removeEventListener('abort', onAbort);
  }
}
